import { CommonModule } from '@angular/common';
import { Component, DestroyRef, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, combineLatest, filter, interval, startWith, switchMap, takeWhile, tap } from 'rxjs';

import { LoaderComponent } from './loader/loader.component';
import { CountdownService } from '../../services/countdown.service';

@Component({
    selector: 'app-count-down',
    imports: [CommonModule, LoaderComponent],
    templateUrl: './countdown.component.html',
    styleUrl: './countdown.component.scss'
})
export class CountDownComponent implements OnInit {
    private static readonly secondsToMsFactor = 1000;
    private static readonly updateIntervalMs = 200;
    private static readonly startingHue = 160;
    private static readonly fullCircle = 360;

    @Input()
    initialTimeInSeconds!: number;

    @Output()
    readonly countDownProgressed = new EventEmitter<number>();

    protected remainingTimeInSeconds!: number;
    protected remainingAmount = CountDownComponent.fullCircle;
    protected color = '';

    constructor(
        private readonly destroyRef: DestroyRef,
        private readonly countdown: CountdownService
    ) {}

    ngOnInit(): void {
        this.countdown.resume();

        // Counts down now and again from the full time on every restart. It must end with the component:
        // CountdownService lives as long as the app and would otherwise keep every countdown ever shown.
        this.countdown.restartCountdown$
            .pipe(
                startWith(undefined),
                switchMap(() => this.countDown()),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    private countDown(): Observable<unknown> {
        const endTime = Date.now() + CountDownComponent.updateIntervalMs + this.secondsToMs(this.initialTimeInSeconds);
        this.remainingTimeInSeconds = this.initialTimeInSeconds;

        return combineLatest([interval(CountDownComponent.updateIntervalMs), this.countdown.pauseCountdown$]).pipe(
            filter(([, isPaused]) => !isPaused),
            tap(() => this.updateProgressValues(endTime)),
            takeWhile(() => Date.now() < endTime),
            tap({
                complete: () => {
                    this.remainingAmount = CountDownComponent.fullCircle;
                }
            })
        );
    }

    private secondsToMs(seconds: number): number {
        return seconds * CountDownComponent.secondsToMsFactor;
    }

    private updateProgressValues(endTime: number): void {
        // When it's 0 / 0, the result will be NaN, so we need to handle that (NaN || 0).
        const remainingRatio = Math.max((endTime - Date.now()) / this.secondsToMs(this.initialTimeInSeconds), 0) || 0;

        this.remainingAmount = CountDownComponent.fullCircle * remainingRatio;
        this.remainingTimeInSeconds = Math.ceil(remainingRatio * this.initialTimeInSeconds);

        const hue = CountDownComponent.startingHue * remainingRatio;
        // eslint-disable-next-line spellcheck/spell-checker
        this.color = `hsl(${hue}, 100%, 50%)`;

        this.countDownProgressed.emit(remainingRatio);
    }
}
